import React, { useState, useEffect, useCallback } from 'react';
import { X, History as HistoryIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNarrowLayout } from '../hooks/use-narrow-layout';

/**
 * StandardRightSidebar - Componente estándar de right sidebar según ACV-SystemDesign.md
 * Para historial de conversaciones y paneles contextuales
 * Soporta ocultable, responsive, y variables CSS del tema
 * Para AIRecording: NO incluye funcionalidad de expansión
 */
interface StandardRightSidebarProps {
  title?: string;
  defaultOpen?: boolean;
  storageKey?: string;
  children?: React.ReactNode;
  onOpenChange?: (open: boolean) => void;
  toggleButton?: React.ReactNode;
  hideFloatingToggle?: boolean;
}

const StandardRightSidebar: React.FC<StandardRightSidebarProps> = ({
  title,
  defaultOpen = false,
  storageKey = 'acv:rightSidebarState',
  children,
  onOpenChange,
  toggleButton,
  hideFloatingToggle = false
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(() => {
    if (typeof window !== 'undefined' && storageKey) {
      const saved = localStorage.getItem(storageKey);
      return saved === 'open' || (saved === null && defaultOpen);
    }
    return defaultOpen;
  });
  const narrow = useNarrowLayout();

  const toggleOpen = useCallback(() => {
    setIsOpen(prev => {
      const newValue = !prev;
      if (typeof window !== 'undefined' && storageKey) {
        localStorage.setItem(storageKey, newValue ? 'open' : 'closed');
      }
      if (onOpenChange) {
        onOpenChange(newValue);
      }
      // Emitir evento para sincronización
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('right-sidebar-state-change', { 
          detail: { isOpen: newValue } 
        }));
      }
      return newValue;
    });
  }, [storageKey, onOpenChange]);

  // Permitir que ControlsBar controle el sidebar
  useEffect(() => {
    const onToggle = () => toggleOpen();
    window.addEventListener('acv-toggle-right-sidebar', onToggle as EventListener);
    return () => window.removeEventListener('acv-toggle-right-sidebar', onToggle as EventListener);
  }, [toggleOpen]);

  // Ajustar el padding-right del main content cuando el sidebar está abierto
  useEffect(() => {
    if (!narrow && isOpen) {
      const sidebarWidth = 320; // Ancho estándar de right sidebar
      if (typeof document !== 'undefined') {
        // Ajustar padding del main content en lugar del body
        const mainElement = document.querySelector('main');
        if (mainElement) {
          mainElement.style.paddingRight = `${sidebarWidth}px`;
          mainElement.style.transition = 'padding-right 0.3s ease';
        }
        return () => {
          if (mainElement) {
            mainElement.style.paddingRight = '';
          }
        };
      }
    } else {
      if (typeof document !== 'undefined') {
        const mainElement = document.querySelector('main');
        if (mainElement) {
          mainElement.style.paddingRight = '';
        }
      }
    }
  }, [isOpen, narrow]);

  return (
    <>
      {/* Overlay para móviles */}
      {narrow && isOpen && (
        <div
          className="sidebar-overlay"
          onClick={toggleOpen}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'var(--right-sidebar-overlay-bg, rgba(0, 0, 0, 0.5))',
            zIndex: 'var(--right-sidebar-overlay-z-index, 998)',
            opacity: 1,
            visibility: 'visible',
            transition: 'opacity 0.3s ease, visibility 0.3s ease',
          }}
        />
      )}

      {/* Right Sidebar */}
      <aside
        className={`right-sidebar ${!isOpen ? 'hidden' : ''}`}
        style={{
          position: 'fixed',
          top: 'var(--acv-embed-top-offset, 0px)',
          right: 0,
          bottom: 0,
          height: 'calc(100vh - var(--acv-embed-top-offset, 0px))',
          width: isOpen ? 'var(--right-sidebar-width, 320px)' : '0px',
          backgroundColor: 'var(--card-bg, var(--bg-primary, #ffffff))',
          opacity: 1,
          color: 'var(--right-sidebar-foreground, var(--text-primary, #1a1a1a))',
          borderLeft: 'var(--right-sidebar-border, 1px solid #e0e0e0)',
          boxShadow: 'var(--right-sidebar-shadow, -2px 0 8px rgba(0, 0, 0, 0.08))',
          zIndex: 'var(--right-sidebar-z-index, 999)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transform: narrow && !isOpen ? 'translateX(100%)' : 'translateX(0)',
          transition: 'transform var(--right-sidebar-transition-duration, 300ms) var(--right-sidebar-transition-timing, cubic-bezier(0.4, 0, 0.2, 1)), width var(--right-sidebar-transition-duration, 300ms) var(--right-sidebar-transition-timing, cubic-bezier(0.4, 0, 0.2, 1))',
        }}
      >
        {/* Header del Sidebar */}
        <div
          className="right-sidebar-header"
          style={{
            padding: 'var(--right-sidebar-header-padding, 16px)',
            borderBottom: 'var(--right-sidebar-header-border, 1px solid #e0e0e0)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: 'var(--card-bg, var(--bg-primary, #ffffff))',
            opacity: 1,
            minHeight: 'var(--right-sidebar-header-height, 64px)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
            <HistoryIcon 
              size={16} 
              style={{ 
                color: 'var(--right-sidebar-icon-color, var(--text-primary, #1a1a1a))' 
              }} 
            />
            <h2
              style={{
                fontSize: 'var(--right-sidebar-title-font-size, 0.875rem)',
                fontWeight: 'var(--right-sidebar-title-font-weight, 500)',
                color: 'var(--right-sidebar-title-color, var(--text-primary, #1a1a1a))',
                margin: 0,
              }}
            >
              {title}
            </h2>
          </div>
          <button
            onClick={toggleOpen}
            className="right-sidebar-close-button"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--right-sidebar-icon-color, var(--text-primary, #1a1a1a))',
              transition: 'background-color 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--right-sidebar-item-hover-bg, #f5f5f5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            aria-label={t('sidebar.close_panel', 'Close panel')}
          >
            <X size={16} />
          </button>
        </div>

        {/* Contenido del Sidebar */}
        <div
          className="right-sidebar-content"
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: 'var(--right-sidebar-content-padding, 16px)',
            minHeight: 0, // Permite que flex funcione correctamente
            // Estilos de scrollbar según ACV-SystemDesign.md
            scrollbarWidth: 'thin', // Firefox
            scrollbarColor: 'var(--scrollbar-thumb-bg, hsl(214.3 31.8% 85%)) var(--scrollbar-track-bg, hsl(210 40% 98%))',
          }}
        >
          {children}
        </div>
        
        {/* Estilos de scrollbar para Webkit (Chrome, Safari, Edge) */}
        <style dangerouslySetInnerHTML={{ __html: `
          .right-sidebar {
            background-color: var(--card-bg, var(--bg-primary, #ffffff)) !important;
            opacity: 1 !important;
          }
          .right-sidebar-header {
            background-color: var(--card-bg, var(--bg-primary, #ffffff)) !important;
            opacity: 1 !important;
          }
          .right-sidebar-content::-webkit-scrollbar {
            width: var(--scrollbar-width, 12px);
            height: var(--scrollbar-height, 12px);
          }
          
          .right-sidebar-content::-webkit-scrollbar-track {
            background-color: var(--scrollbar-track-bg, hsl(210 40% 98%));
            border-radius: var(--scrollbar-track-border-radius, 4px);
          }
          
          .right-sidebar-content::-webkit-scrollbar-thumb {
            background-color: var(--scrollbar-thumb-bg, hsl(214.3 31.8% 85%));
            border-radius: var(--scrollbar-thumb-border-radius, 4px);
            transition: background-color 0.2s ease;
          }
          
          .right-sidebar-content::-webkit-scrollbar-thumb:hover {
            background-color: var(--scrollbar-thumb-hover-bg, hsl(214.3 31.8% 75%));
          }
        `}} />
      </aside>

      {/* Toggle Button Flotante - solo se muestra cuando está cerrado */}
      {!isOpen && !hideFloatingToggle && (
        <button
          onClick={toggleOpen}
          className="right-sidebar-toggle"
          style={{
            position: 'fixed',
            right: 'var(--right-sidebar-toggle-right, 16px)',
            top: 'calc(var(--acv-embed-top-offset, 0px) + var(--right-sidebar-toggle-top, 16px))',
            width: 'var(--right-sidebar-toggle-size, 32px)',
            height: 'var(--right-sidebar-toggle-size, 32px)',
            borderRadius: 'var(--right-sidebar-toggle-border-radius, 50%)',
            backgroundColor: 'var(--right-sidebar-toggle-bg, transparent)',
            color: 'var(--right-sidebar-toggle-foreground, var(--text-primary, #1a1a1a))',
            border: 'none',
            boxShadow: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 'var(--right-sidebar-toggle-z-index, 997)',
            transition: 'background-color 0.2s ease',
            padding: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--right-sidebar-toggle-hover-bg, rgba(0, 0, 0, 0.05))';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--right-sidebar-toggle-bg, transparent)';
          }}
          aria-label={t('sidebar.open_history', 'Open history')}
        >
          {toggleButton || <HistoryIcon size={18} />}
        </button>
      )}
    </>
  );
};

export default StandardRightSidebar;

